import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Card, CardContent, Container, Grid, Typography, Button, Divider, CircularProgress, MenuItem, Select, FormControl, InputLabel } from '@material-ui/core';
import { Formik } from 'formik';
import * as yup from 'yup';
import Cards from 'react-credit-cards';
import 'react-credit-cards/es/styles-compiled.css';
import NewFormTextField, { maskedCreditCard, maskedValidDate, maskedCvc } from 'apps/paynet/components/FormTextField';
import { Api, showToast, formatTRYSetting, LinkModal } from 'core';
import { useDispatch } from 'react-redux';
import { PaymentSourceEnum } from 'core';

const creditCardFormSchema = yup.object({
  cardHolder: yup.string().required('Boş olamaz!'),
  cardNumber: yup.string().transform((v) => v.replaceAll('-', '').replaceAll('_', '')).required('Boş olamaz!').length(16, '16 haneli olmalıdır!'),
  cvc: yup.string().transform((v) => v.replaceAll('_', '')).required('Boş olamaz!'),
  validDate: yup.string().transform((v) => v.replaceAll('_', '').replaceAll('/', '')).required('Boş olamaz!').length(4, 'AA/YY formatında olmalıdır! (02/25)'),
});

const CustomerPaymentPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [linkData, setLinkData] = useState<any>(null);

  const [creditCardNumber, setCreditCardNumber] = useState('');
  const [focusedField, setFocusedField] = useState<any>('');
  
  const [instalmentLoading, setInstalmentLoading] = useState(false);
  const [bankInfo, setBankInfo] = useState<any>(null);
  const [selectedInstalmentKey, setSelectedInstalmentKey] = useState<string>('');

  const [openModal, setOpenModal] = useState(false);
  const [postUrl, setPostUrl] = useState('');
  const [htmlContent, setHtmlContent] = useState<string | undefined>(undefined);

  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    const fetchLink = async () => {
      try {
        const response = await Api.PaymentLink.validate(token);
        if (response.isSuccess && response.result) {
          setLinkData(response.result);
          // SET BEARER TOKEN TO SCOPED ACCESS TOKEN
          Api.setBearerToken(response.result.accessToken);
        } else {
          setError(response.errorMessage || 'Link geçersiz veya süresi dolmuş.');
        }
      } catch (err: any) {
        setError(err.message || 'Bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };
    fetchLink();
  }, [token]);

  const onBlurCreditCardNumber = (e: any) => {
    const num = e.target.value?.replaceAll('-', '').replaceAll('_', '');
    setCreditCardNumber(num);
  };

  useEffect(() => {
    if (creditCardNumber && creditCardNumber.length >= 6 && linkData) {
      fetchInstalments();
    }
  }, [creditCardNumber]);

  const fetchInstalments = async () => {
    if (!linkData || !linkData.paymentProviders || linkData.paymentProviders.length === 0) return;
    
    setInstalmentLoading(true);
    try {
      const provider = linkData.paymentProviders[0];
      const requestDto = {
        addCommission: true, // Default to true or what merchant specified, but Customer Payment usually includes commission
        amount: linkData.amount,
        cardNumber: creditCardNumber,
        cardType: 'cc'
      };
      const response = await Api.Paynet.getInstalmentInfo(requestDto as any, provider);
      if (response.isSuccess && response.result && response.result.data && response.result.data.length > 0) {
        setBankInfo(response.result.data[0]);
        // Default to tek çekim
        const tekCekim = response.result.data[0].ratios.find((r: any) => r.instalment <= 1);
        if (tekCekim) {
          setSelectedInstalmentKey(tekCekim.instalmentKey);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setInstalmentLoading(false);
    }
  };

  const onPaymentSubmit = async (values: any) => {
    if (!selectedInstalmentKey) {
      dispatch(showToast({ message: 'Lütfen taksit seçeneği belirleyin.', severity: 'warning' }));
      return;
    }

    setPaymentLoading(true);
    const selectedRatio = bankInfo.ratios.find((r: any) => r.instalmentKey === selectedInstalmentKey);
    const provider = linkData.paymentProviders[0];

    const formData = new FormData();
    formData.append('addCommission', 'true');
    formData.append('amount', selectedRatio.totalAmount.toString());
    formData.append('cardHolder', values.cardHolder);
    formData.append('cardNumber', values.cardNumber.replaceAll('-', '').replaceAll('_', ''));
    formData.append('cvc', values.cvc);
    formData.append('month', values.validDate.slice(0, 2));
    formData.append('year', values.validDate.slice(2, 4));
    formData.append('instalmentKey', selectedInstalmentKey);
    formData.append('instalment', selectedRatio.instalment.toString());
    formData.append('paymentTransactionId', linkData.paymentTransactionId.toString());

    try {
      const response = await Api.Paynet.tdsInitialPayment(formData, provider);
      if (response.isSuccess && response.result) {
        setPostUrl(response.result.postUrl);
        setHtmlContent(response.result.htmlContent);
        setOpenModal(true);
      } else {
        dispatch(showToast({ message: response.errorMessage || 'Ödeme başlatılamadı.', severity: 'error' }));
      }
    } catch (err: any) {
      dispatch(showToast({ message: err.message || 'Bir hata oluştu.', severity: 'error' }));
    } finally {
      setPaymentLoading(false);
    }
  };

  if (loading) return <Box p={4} display="flex" justifyContent="center"><CircularProgress /></Box>;
  
  if (error) return (
    <Container maxWidth="sm" style={{ marginTop: 64 }}>
      <Card>
        <CardContent>
          <Typography color="error" variant="h5" align="center">{error}</Typography>
        </CardContent>
      </Card>
    </Container>
  );

  return (
    <Container maxWidth="md" style={{ marginTop: 64, marginBottom: 64 }}>
      <Card>
        <CardContent>
          <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
            {linkData.merchantLogoUrl && <img src={linkData.merchantLogoUrl} alt={linkData.merchantName} style={{ maxHeight: 80, marginBottom: 16 }} />}
            <Typography variant="h4" gutterBottom>{linkData.merchantName}</Typography>
            <Typography variant="subtitle1" color="textSecondary">{linkData.description}</Typography>
            <Typography variant="h3" color="primary" style={{ marginTop: 16, fontWeight: 700 }}>
              {formatTRYSetting.format(linkData.amount)}
            </Typography>
            {linkData.customerName && <Typography variant="body1" style={{ marginTop: 8 }}>Sayın {linkData.customerName}, bu ödeme sizin adınıza oluşturulmuştur.</Typography>}
          </Box>
          <Divider />
          <Box mt={4}>
            <Formik
              initialValues={{ cardHolder: '', cardNumber: '', cvc: '', validDate: '' }}
              validationSchema={creditCardFormSchema}
              onSubmit={onPaymentSubmit}
            >
              {({ handleSubmit, handleChange, values, errors }) => (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <Cards
                        cvc={values.cvc}
                        expiry={values.validDate}
                        name={values.cardHolder}
                        number={values.cardNumber}
                        focused={focusedField}
                        placeholders={{ name: 'AD SOYAD' }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <NewFormTextField
                            name="cardNumber"
                            label="Kart Numarası"
                            placeholder="Kart Numarası"
                            value={values.cardNumber}
                            onChange={handleChange}
                            onBlur={onBlurCreditCardNumber}
                            onFocus={() => setFocusedField('number')}
                            error={!!errors.cardNumber}
                            helperText={errors.cardNumber as string}
                            InputProps={{ inputComponent: maskedCreditCard }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <NewFormTextField
                            name="cardHolder"
                            label="Kart Üzerindeki İsim"
                            placeholder="Kart Üzerindeki İsim"
                            value={values.cardHolder}
                            onChange={handleChange}
                            onFocus={() => setFocusedField('name')}
                            error={!!errors.cardHolder}
                            helperText={errors.cardHolder as string}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <NewFormTextField
                            name="validDate"
                            label="Son Kullanma (AA/YY)"
                            placeholder="AA/YY"
                            value={values.validDate}
                            onChange={handleChange}
                            onFocus={() => setFocusedField('expiry')}
                            error={!!errors.validDate}
                            helperText={errors.validDate as string}
                            InputProps={{ inputComponent: maskedValidDate }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <NewFormTextField
                            name="cvc"
                            label="CVC"
                            placeholder="CVC"
                            value={values.cvc}
                            onChange={handleChange}
                            onFocus={() => setFocusedField('cvc')}
                            error={!!errors.cvc}
                            helperText={errors.cvc as string}
                            InputProps={{ inputComponent: maskedCvc }}
                          />
                        </Grid>
                      </Grid>

                      {instalmentLoading && <Box mt={2}><CircularProgress size={24} /></Box>}
                      
                      {bankInfo && bankInfo.ratios && bankInfo.ratios.length > 0 && (
                        <Box mt={3}>
                          <FormControl variant="outlined" fullWidth>
                            <InputLabel>Taksit Seçenekleri</InputLabel>
                            <Select
                              value={selectedInstalmentKey}
                              onChange={(e) => setSelectedInstalmentKey(e.target.value as string)}
                              label="Taksit Seçenekleri"
                            >
                              {bankInfo.ratios.map((r: any) => (
                                <MenuItem key={r.instalmentKey} value={r.instalmentKey}>
                                  {r.instalment <= 1 ? 'Tek Çekim' : `${r.instalment} Taksit`} - Toplam: {r.totalAmount.toFixed(2)} ₺
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      )}

                      <Box mt={4}>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          fullWidth
                          size="large"
                          disabled={paymentLoading || !selectedInstalmentKey}
                        >
                          {paymentLoading ? <CircularProgress size={24} /> : 'Ödemeyi Tamamla'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          </Box>
        </CardContent>
      </Card>
      {openModal && (
        <LinkModal isOpen={openModal} onClose={() => setOpenModal(false)} href={postUrl} htmlContent={htmlContent} />
      )}
    </Container>
  );
};

export default CustomerPaymentPage;
